import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSalesOverview, type SalesOverviewResponse } from "../../lib/sales-overview";
import { formatCurrency, formatDateTime } from "../../lib/utils";

function paymentTypeLabel(value: string) {
  switch (value) {
    case "MPESA":
      return "M-Pesa";
    case "CASH":
      return "Cash";
    case "CHEQUE":
      return "Cheque";
    case "BANK_TRANSFER":
      return "Bank Transfer";
    default:
      return value || "Not recorded";
  }
}

function buildTrendPath(values: number[], width: number, height: number) {
  if (values.length === 0) {
    return "";
  }

  const max = Math.max(...values, 1);
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

const emptyOverview: SalesOverviewResponse = {
  metrics: {
    totalOrders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    thisMonthRevenue: 0,
    outstandingBalance: 0,
    customersWithOrders: 0,
    activeCoupons: 0,
    lowStockProducts: 0
  },
  dailySeries: [],
  paymentMix: [],
  recentOrders: [],
  recentCustomers: [],
  activeCoupons: []
};

export function SalesDashboardPage() {
  const [data, setData] = useState<SalesOverviewResponse>(emptyOverview);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDayIndex, setActiveDayIndex] = useState<number>(-1);
  const [activePaymentType, setActivePaymentType] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchSalesOverview();
        if (!ignore) {
          setData(result);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load sales overview.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, []);

  const headlineCards = useMemo(
    () => [
      {
        label: "This month revenue",
        value: formatCurrency(data.metrics.thisMonthRevenue),
        detail: `${data.metrics.paidOrders} orders settled`,
        tone: "from-cyan-500/16 via-white/40 to-transparent"
      },
      {
        label: "Outstanding balance",
        value: formatCurrency(data.metrics.outstandingBalance),
        detail: `${data.metrics.pendingOrders} pending orders`,
        tone: "from-amber-500/16 via-white/40 to-transparent"
      },
      {
        label: "Buying customers",
        value: String(data.metrics.customersWithOrders),
        detail: `${data.metrics.totalOrders} total sales orders`,
        tone: "from-emerald-500/16 via-white/40 to-transparent"
      },
      {
        label: "Offers and stock risk",
        value: `${data.metrics.activeCoupons} / ${data.metrics.lowStockProducts}`,
        detail: "Active coupons / low-stock products",
        tone: "from-violet-500/16 via-white/40 to-transparent"
      }
    ],
    [data.metrics]
  );

  const dailyValues = data.dailySeries.map((item) => item.paidRevenue);
  const dailyPath = useMemo(() => buildTrendPath(dailyValues, 560, 180), [dailyValues]);
  const resolvedDayIndex =
    activeDayIndex >= 0 && activeDayIndex < data.dailySeries.length ? activeDayIndex : data.dailySeries.length - 1;
  const activeDay = resolvedDayIndex >= 0 ? data.dailySeries[resolvedDayIndex] : null;
  const activePayment =
    data.paymentMix.find((item) => item.paymentType === activePaymentType) || data.paymentMix[0] || null;
  const paymentMixTotal = data.paymentMix.reduce((sum, item) => sum + item.collectedAmount, 0);

  return (
    <div className="space-y-6 pb-6">
      <div className="relative overflow-hidden rounded-[36px] border border-white/10 bg-slate-950 px-6 py-7 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.16),transparent_28%),linear-gradient(135deg,rgba(15,23,42,0.92),rgba(2,6,23,0.97))]" />
        <div className="relative flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales overview</p>
            <h1 className="mt-3 max-w-4xl text-4xl font-semibold tracking-tight">Sales overview</h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
              Revenue, collections, customers, and active offers.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/sales/orders">
              Open orders
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/sales/payments">
              Work payments
            </Link>
            <Link className="rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold transition hover:bg-white/14" to="/sales/customers">
              View customers
            </Link>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {headlineCards.map((card) => (
          <div className="admin-card relative overflow-hidden rounded-[30px] p-5" key={card.label}>
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${card.tone}`} />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-nv-slate">{card.label}</p>
              <p className="mt-4 break-words text-3xl font-semibold tracking-tight text-nv-ink">{card.value}</p>
              <p className="mt-3 text-sm text-nv-slate">{card.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="items-start grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.95fr)]">
        <div className="admin-card min-w-0 rounded-[32px] p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Collections trend</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Paid revenue over the last 14 days</h2>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                {activeDay ? `Selected day: ${activeDay.dayLabel}` : "Daily collections"}
              </p>
              <p className="mt-2 text-xl font-semibold text-nv-ink">
                {activeDay ? formatCurrency(activeDay.paidRevenue) : formatCurrency(0)}
              </p>
              <p className="mt-1 text-xs text-nv-slate">
                {activeDay ? `${activeDay.paidOrders} paid orders` : "Hover a point for detail"}
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-[28px] border border-black/6 bg-[linear-gradient(180deg,rgba(8,145,178,0.08),rgba(255,255,255,0))] p-4 dark:border-white/8">
            {loading ? (
              <div className="flex h-[230px] items-center justify-center text-sm text-nv-slate">Loading revenue chart...</div>
            ) : data.dailySeries.length > 0 ? (
              <div className="min-w-0">
                <svg className="h-[230px] w-full" viewBox="0 0 560 230" preserveAspectRatio="none" role="img">
                  <defs>
                    <linearGradient id="salesOverviewStroke" x1="0%" x2="100%" y1="0%" y2="0%">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id="salesOverviewFill" x1="0%" x2="0%" y1="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(16,185,129,0.22)" />
                      <stop offset="100%" stopColor="rgba(16,185,129,0.02)" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3].map((line) => (
                    <line
                      key={line}
                      x1="0"
                      x2="560"
                      y1={20 + line * 50}
                      y2={20 + line * 50}
                      stroke="rgba(90,103,120,0.18)"
                      strokeDasharray="4 8"
                    />
                  ))}
                  <path d={`${dailyPath} L 560 180 L 0 180 Z`} fill="url(#salesOverviewFill)" transform="translate(0 20)" />
                  <path d={dailyPath} fill="none" stroke="url(#salesOverviewStroke)" strokeLinecap="round" strokeWidth="4" transform="translate(0 20)" />
                  {data.dailySeries.map((point, index) => {
                    const max = Math.max(...dailyValues, 1);
                    const x = data.dailySeries.length === 1 ? 280 : (index / (data.dailySeries.length - 1)) * 560;
                    const y = 20 + (180 - (point.paidRevenue / max) * 180);
                    const active = index === resolvedDayIndex;

                    return (
                      <g key={point.dayKey}>
                        <circle
                          cx={x}
                          cy={y}
                          fill="transparent"
                          onFocus={() => setActiveDayIndex(index)}
                          onMouseEnter={() => setActiveDayIndex(index)}
                          r="16"
                          tabIndex={0}
                        />
                        <circle cx={x} cy={y} fill="#ffffff" r={active ? "7" : "5"} stroke="#0f172a" strokeWidth={active ? "4" : "3"} />
                      </g>
                    );
                  })}
                </svg>

                <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-nv-slate sm:grid-cols-4 xl:grid-cols-7">
                  {data.dailySeries.map((point, index) => (
                    <button
                      className={`min-w-0 rounded-full px-3 py-2 text-center transition ${index === resolvedDayIndex ? "bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300" : "bg-black/[0.03] hover:bg-black/[0.05] dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"}`}
                      key={point.dayKey}
                      onClick={() => setActiveDayIndex(index)}
                      type="button"
                    >
                      <span className="block truncate">{point.dayLabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[230px] items-center justify-center text-sm text-nv-slate">No recent paid revenue yet.</div>
            )}
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Payment mix</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">This month by method</h2>
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">This month collected</p>
          </div>

          {loading ? (
            <div className="mt-6 text-sm text-nv-slate">Loading payment mix...</div>
          ) : activePayment ? (
            <>
              <div className="mt-6 rounded-[28px] bg-[linear-gradient(135deg,rgba(14,165,233,0.1),rgba(255,255,255,0.55))] p-5 dark:bg-[linear-gradient(135deg,rgba(14,165,233,0.16),rgba(255,255,255,0.03))]">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate">Selected method</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{paymentTypeLabel(activePayment.paymentType)}</p>
                <p className="mt-3 text-sm text-nv-slate">{formatCurrency(activePayment.collectedAmount)} collected across {activePayment.totalOrders} orders</p>
              </div>

              <div className="mt-5 space-y-3">
                {data.paymentMix.map((item) => {
                  const share = paymentMixTotal > 0 ? (item.collectedAmount / paymentMixTotal) * 100 : 0;
                  const active = item.paymentType === activePayment.paymentType;
                  return (
                    <button
                      className={`w-full rounded-[24px] border p-4 text-left transition ${active ? "border-cyan-300 bg-cyan-50/70 dark:border-cyan-700 dark:bg-cyan-950/30" : "border-black/8 bg-transparent hover:bg-black/[0.03] dark:border-white/8 dark:hover:bg-white/[0.03]"}`}
                      key={item.paymentType || "unrecorded"}
                      onClick={() => setActivePaymentType(item.paymentType)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-nv-ink">{paymentTypeLabel(item.paymentType)}</p>
                          <p className="mt-1 text-sm text-nv-slate">{item.totalOrders} orders</p>
                        </div>
                        <p className="text-sm font-semibold text-nv-ink">{formatCurrency(item.collectedAmount)}</p>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/6 dark:bg-white/8">
                        <div className="h-full rounded-full bg-[linear-gradient(90deg,#06b6d4,#10b981)]" style={{ width: `${share}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
              No collected payments recorded yet.
            </div>
          )}
        </div>
      </div>

      <div className="items-start grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.9fr)_minmax(0,0.9fr)]">
        <div className="admin-card rounded-[32px] p-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Recent orders</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Latest sales activity</h2>
            </div>
            <Link className="text-sm font-semibold text-cyan-600 dark:text-cyan-300" to="/sales/orders">
              Open queue
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading recent orders...</div>
            ) : data.recentOrders.length > 0 ? (
              data.recentOrders.map((order) => (
                <div className="field-soft rounded-[24px] p-4" key={order.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-nv-ink">Order #{order.id}</p>
                      <p className="mt-1 text-sm text-nv-slate">{order.customerName}</p>
                    </div>
                    <p className="text-sm font-semibold text-nv-ink">{formatCurrency(order.total)}</p>
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-nv-slate sm:grid-cols-2">
                    <p>{order.addedOn ? formatDateTime(order.addedOn) : "Unknown date"}</p>
                    <p>{paymentTypeLabel(order.paymentType)}</p>
                    <p>Paid {formatCurrency(order.amountPaid)}</p>
                    <p>Balance {formatCurrency(order.balance)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                No sales orders yet.
              </div>
            )}
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Customers</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Recently active buyers</h2>
            </div>
            <Link className="text-sm font-semibold text-cyan-600 dark:text-cyan-300" to="/sales/customers">
              Open customers
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading recent customers...</div>
            ) : data.recentCustomers.length > 0 ? (
              data.recentCustomers.map((customer) => (
                <div className="field-soft rounded-[24px] p-4" key={customer.id}>
                  <p className="font-semibold text-nv-ink">{customer.name}</p>
                  <div className="mt-2 grid gap-2 text-sm text-nv-slate">
                    <p>{customer.phone || customer.email || "No contact recorded"}</p>
                    <p>{customer.lastOrderOn ? `Last order ${formatDateTime(customer.lastOrderOn)}` : "No order date"}</p>
                    <p>{customer.orderCount} orders</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                No active customers yet.
              </div>
            )}
          </div>
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Offers</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Active coupons</h2>
            </div>
            <Link className="text-sm font-semibold text-cyan-600 dark:text-cyan-300" to="/sales/coupons">
              Open promotions
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-nv-slate">Loading active offers...</div>
            ) : data.activeCoupons.length > 0 ? (
              data.activeCoupons.map((coupon) => (
                <div className="field-soft rounded-[24px] p-4" key={coupon.id}>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-nv-ink">{coupon.code}</p>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700 dark:text-cyan-300">
                      {coupon.type === "FIXED" ? formatCurrency(coupon.discount) : `${coupon.discount}%`}
                    </p>
                  </div>
                  <div className="mt-2 grid gap-2 text-sm text-nv-slate">
                    <p>{coupon.description || "No description provided."}</p>
                    <p>{coupon.expiryDate ? `Expires ${formatDateTime(coupon.expiryDate)}` : "No expiry date"}</p>
                    <p>Used {coupon.usageCount} times</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                No active coupons right now.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
